/**
 * Static catalog used by the Phase-1 mocks. Mirrors what the real
 * dim_pricelist / dim_office / dim_agency etc. tables give us — kept
 * small and hand-picked so the prototype stays readable.
 */

// ACRISS macros with SIPPs (aligned with dim_category_macro seed).
export const ACRISS_TREE = [
	{ code: 'mini',         name: 'Mini',         sipps: ['MBMR','MDMR','MCMR'] },
	{ code: 'economy',      name: 'Economy',      sipps: ['EBMR','EDMR','ECMR','EDAR'] },
	{ code: 'compact',      name: 'Compact',      sipps: ['CDMR','CCMR','CDAR','CLMR','CFAR'] },
	{ code: 'intermediate', name: 'Intermediate', sipps: ['IDMR','ICMR','IDAR','ILAR','IFAR','IGMR'] },
	{ code: 'standard',     name: 'Standard',     sipps: ['SDMR','SDAR','SWAR'] },
	{ code: 'fullsize',     name: 'Full-size',    sipps: ['FDMR','FDAR','FVMR','FVAR','FFAR'] },
	{ code: 'premium',      name: 'Premium',      sipps: ['PTAR','PDAR'] },
	{ code: 'luxury',       name: 'Luxury',       sipps: ['LDAR','LFAR','LTAR'] },
	{ code: 'suv',          name: 'SUV',          sipps: ['SFAR','IFAR','PFAR'] },
	{ code: 'van',          name: 'Van / MPV',    sipps: ['FVMR','SVMR'] },
	{ code: 'special',      name: 'Special',      sipps: ['XFAR'] },
]

// Flat list of all SIPPs in the tree — used by consistency rule editor.
export const ALL_SIPPS = [...new Set(ACRISS_TREE.flatMap(m => m.sipps))].sort()

export const OFFICES = [
	{ code: 'LPA-AP',  name: 'Gran Canaria Airport' },
	{ code: 'LPA-CEN', name: 'Las Palmas City Center' },
	{ code: 'TFN-AP',  name: 'Tenerife North Airport' },
	{ code: 'TFS-AP',  name: 'Tenerife South Airport' },
	{ code: 'FUE-AP',  name: 'Fuerteventura Airport' },
	{ code: 'ACE-AP',  name: 'Lanzarote Airport' },
]

export const OFFICE_GROUPS = [
	{ code: 'airports',      name: 'Airports',     offices: ['LPA-AP','TFN-AP','TFS-AP','FUE-AP','ACE-AP'] },
	{ code: 'gc',            name: 'Gran Canaria', offices: ['LPA-AP','LPA-CEN'] },
	{ code: 'tenerife',      name: 'Tenerife',     offices: ['TFN-AP','TFS-AP'] },
	{ code: 'east_islands',  name: 'East Islands', offices: ['FUE-AP','ACE-AP'] },
]

export const DURATION_BUCKETS = [
	{ code: '1-3',  name: '1-3 days',  min: 1,  max: 3 },
	{ code: '4-7',  name: '4-7 days',  min: 4,  max: 7 },
	{ code: '8-14', name: '8-14 days', min: 8,  max: 14 },
	{ code: '15+',  name: '15+ days',  min: 15, max: null },
]

// Sites the operator connects with the PMS through rate cards.
export const SITES = [
	{ code: 'discovercars', name: 'DiscoverCars' },
	{ code: 'cartrawler',   name: 'CarTrawler' },
	{ code: 'rentalcars',   name: 'Rentalcars.com' },
	{ code: 'sunnycars',    name: 'Sunny Cars' },
	{ code: 'holidayautos', name: 'Holiday Autos' },
	{ code: 'own_web',      name: 'Own Website' },
]

// Dimensions that identify a rate-card FACE.
// Rate Cards v2: (site + tariff_type + insurance_type + franchise) →
// 1 read pricelist + N write pricelists.
// TARIFF_TYPES / INSURANCE_TYPES / FRANCHISES lived here until
// 2026-09-24. They were three hardcoded slug lists that nothing
// reconciled with Price Seeker, so a face could name an insurance that
// did not exist and fail to match anything, silently. The axes are
// Price Seeker ids now and their values come from the tenant's own
// catalogue — see `@services/axes`.

// Real dim_pricelist snapshot from canarias.com (90 pricelists, Sept 2026).
// The real Fase-2 flow will fetch these from `/api/my-business/admin/pricelists`
// but the shape (code / name / currency) is intentionally identical so the
// mock and the real endpoint are drop-in swappable.
import PRICELISTS_JSON from './pricelists.canarias.json'
export const PRICELISTS = PRICELISTS_JSON

// Fake strategies as they would come from the existing Strategies list.
export const STRATEGIES = [
	{ id: 'stg-1', name: 'Summer 2026 · Brokers · Standard/Intermediate' },
	{ id: 'stg-2', name: 'Weekdays · Airports · All categories' },
	{ id: 'stg-3', name: 'Lead-time <7d · Prepaid channels' },
	{ id: 'stg-4', name: 'Own Web · Long rentals 15+' },
]
